/**
 * Workshop venue — catalog via App Proxy GET /apps/workshop-events/events-v;
 * capacity/status/validate via direct public API (EW hybrid model).
 */
(function (global) {
  'use strict';

  var PROD_API_BASE = 'https://adapter.aogen.com.au';
  var LIVE_STATE_PATH = '/api/public/workshop-venue/event-live-state';
  var LIVE_STREAM_PATH = '/api/public/workshop-venue/event-live-stream';
  var VALIDATE_PATH = '/api/public/workshop-venue/capacity/validate';
  var CONFIG_PATH = '/api/public/workshop-booking/config';
  var LIVE_STATE_CHUNK_SIZE = 50;
  var DEFAULT_FETCH_TIMEOUT_MS = 8000;

  function resolveAogenApiBase() {
    var raw =
      (typeof global.__AOGEN_API_BASE__ === 'string' && global.__AOGEN_API_BASE__.trim()) ||
      (typeof global.__WB_FORMS_API_BASE__ === 'string' && global.__WB_FORMS_API_BASE__.trim()) ||
      '';
    raw = String(raw || '').replace(/\/+$/, '');
    return raw || PROD_API_BASE;
  }

  var cachedClientConfig = null;
  var cachedFullConfig = null;
  var configLoadPromise = null;
  var liveStateInFlight = Object.create(null);
  var liveStateRecent = Object.create(null);
  var LIVE_STATE_REUSE_MS = 2800;

  function shopDomain() {
    if (global.Shopify && global.Shopify.shop) return String(global.Shopify.shop);
    return '';
  }

  function normalizeStatus(status) {
    if (status == null || status === '') return '';
    return String(status).toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  }

  function hasValue(value) {
    return value != null && value !== '';
  }

  function eventGidFromSlot(slot) {
    if (!slot) return '';
    return String(
      slot.gid ||
        slot.event_metaobject_gid ||
        slot.canonical_event_id ||
        slot.id ||
        (slot._raw &&
          (slot._raw.gid ||
            slot._raw.event_metaobject_gid ||
            slot._raw.canonical_event_id ||
            slot._raw.id ||
            slot._raw.event_id)) ||
        ''
    ).trim();
  }

  function primaryEventIdForRequest(id) {
    var aliases = eventIdAliases(id);
    if (!aliases.length) return '';
    for (var i = 0; i < aliases.length; i++) {
      if (/^gid:\/\//i.test(aliases[i])) return aliases[i];
    }
    return aliases[0];
  }

  function eventIdsEquivalent(a, b) {
    return liveStateAliasesMatch(a, b);
  }

  function collectEventGids(slots) {
    var gids = [];
    (slots || []).forEach(function (slot) {
      if (!slot) return;
      var candidates = [eventGidFromSlot(slot), slot.canonical_event_id];
      if (slot._raw) {
        if (slot._raw.canonical_event_id) candidates.push(slot._raw.canonical_event_id);
        if (slot._raw.event_metaobject_gid) candidates.push(slot._raw.event_metaobject_gid);
        if (slot._raw.event_id) candidates.push(slot._raw.event_id);
      }
      var primary = '';
      for (var i = 0; i < candidates.length; i++) {
        primary = primaryEventIdForRequest(candidates[i]);
        if (primary) break;
      }
      if (!primary) return;
      var duplicate = false;
      for (var j = 0; j < gids.length; j++) {
        if (eventIdsEquivalent(gids[j], primary)) {
          duplicate = true;
          break;
        }
      }
      if (!duplicate) gids.push(primary);
    });
    return gids;
  }

  function chunkIds(ids, size) {
    var chunks = [];
    for (var i = 0; i < ids.length; i += size) {
      chunks.push(ids.slice(i, i + size));
    }
    return chunks;
  }

  function liveStateUrl() {
    return resolveAogenApiBase() + LIVE_STATE_PATH;
  }

  function liveStreamUrl() {
    return resolveAogenApiBase() + LIVE_STREAM_PATH;
  }

  function validateUrl() {
    return resolveAogenApiBase() + VALIDATE_PATH;
  }

  function configUrl() {
    return resolveAogenApiBase() + CONFIG_PATH;
  }

  function mergeLiveStateResponses(results) {
    var merged = { as_of: null, states: {}, config_version: null, client_config: null, ok: true };
    results.forEach(function (part) {
      if (!part.ok) {
        merged.ok = false;
        return;
      }
      var body = part.body || {};
      if (body.ok === false) {
        merged.ok = false;
        if (body.states && typeof body.states === 'object') {
          Object.assign(merged.states, body.states);
        }
        return;
      }
      if (body.as_of) merged.as_of = body.as_of;
      if (body.config_version != null) merged.config_version = body.config_version;
      if (body.client_config) merged.client_config = body.client_config;
      rememberConfigFromBody(body);
      Object.assign(merged.states, body.states || {});
    });
    merged.states = indexLiveStatesByAlias(merged.states);
    rememberConfigFromBody(merged);
    return merged;
  }

  function indexLiveStatesByAlias(states) {
    var indexed = {};
    if (!states || typeof states !== 'object') return indexed;
    Object.keys(states).forEach(function (key) {
      var entry = states[key];
      if (!entry || typeof entry !== 'object') return;
      eventIdAliases(key).forEach(function (alias) {
        if (!indexed[alias]) indexed[alias] = entry;
      });
      if (entry.canonical_event_id != null) {
        eventIdAliases(entry.canonical_event_id).forEach(function (alias) {
          if (!indexed[alias]) indexed[alias] = entry;
        });
      }
    });
    return indexed;
  }

  function requestLiveStateChunks(url, gids, options, timeoutMs) {
    var chunks = chunkIds(gids, LIVE_STATE_CHUNK_SIZE);
    return Promise.all(
      chunks.map(function (chunk) {
        return postJson(
          url,
          {
            shop_domain: options.shopDomain || shopDomain(),
            event_ids: chunk,
          },
          timeoutMs
        );
      })
    ).then(function (results) {
      return { merged: mergeLiveStateResponses(results), results: results };
    });
  }

  function rememberConfigFromBody(body) {
    if (!body || typeof body !== 'object') return;
    if (body.client_config) {
      var CFG = resolveBookingConfig();
      if (CFG && typeof CFG.mergeClientConfigSections === 'function') {
        cachedClientConfig = CFG.mergeClientConfigSections(cachedClientConfig || {}, body.client_config);
      } else {
        cachedClientConfig = body.client_config;
      }
      if (CFG && typeof CFG.applyClientConfigToCartRoots === 'function') {
        CFG.applyClientConfigToCartRoots(body.client_config);
      }
    }
    if (body.config) cachedFullConfig = body.config;
  }

  function getClientConfig() {
    return cachedClientConfig;
  }

  function getFullConfig() {
    return cachedFullConfig;
  }

  function resolvePollIntervalMs(mode, fallbackMs) {
    var cc = cachedClientConfig;
    if (cc && cc.live_state && cc.live_state.poll) {
      var poll = cc.live_state.poll;
      if (mode === 'booking' && poll.interval_booking_ms > 0) return poll.interval_booking_ms;
      if (mode === 'browsing' && poll.interval_browsing_ms > 0) return poll.interval_browsing_ms;
      if (poll.interval_ms > 0) return poll.interval_ms;
    }
    return fallbackMs != null ? fallbackMs : 5000;
  }

  function shouldValidateBeforeAdd() {
    if (cachedFullConfig && cachedFullConfig.gates) {
      return cachedFullConfig.gates.validate_before_add !== false;
    }
    var CFG = resolveBookingConfig();
    if (CFG && typeof CFG.validateGateEnabled === 'function') {
      return CFG.validateGateEnabled(
        {
          getAttribute: function (name) {
            if (name !== 'data-section-id') return '';
            return global.document && global.document.getElementById('WcfConfig-cart-page') ? 'cart-page' : 'cart-drawer';
          },
        },
        'add_to_cart'
      );
    }
    return true;
  }

  function resolveGatesConfig() {
    if (cachedFullConfig && cachedFullConfig.gates) return cachedFullConfig.gates;
    var CFG = resolveBookingConfig();
    if (CFG && typeof CFG.readBootstrap === 'function') {
      return (CFG.readBootstrap({ getAttribute: function () { return 'cart-drawer'; } }).gates) || {};
    }
    return {};
  }

  function resolvePolicyConfig() {
    if (cachedFullConfig && cachedFullConfig.policy) return cachedFullConfig.policy;
    var CFG = resolveBookingConfig();
    if (CFG && typeof CFG.readBootstrap === 'function') {
      return (CFG.readBootstrap({ getAttribute: function () { return 'cart-drawer'; } }).policy) || {};
    }
    return {};
  }

  function verifySoftMode() {
    var CFG = resolveBookingConfig();
    if (CFG && typeof CFG.verifySoftMode === 'function') return CFG.verifySoftMode();
    return false;
  }

  function validateFailModeOpen() {
    if (verifySoftMode()) return true;
    return String(resolvePolicyConfig().validate_fail_mode || 'closed').toLowerCase() === 'open';
  }

  function liveFetchBeforeAddEnabled() {
    return resolveGatesConfig().live_fetch_before_add !== false;
  }

  function maxStaleMsBeforeAdd() {
    var gates = resolveGatesConfig();
    var ms = gates.max_stale_ms_before_add;
    return ms != null && ms !== '' ? Number(ms) : 15000;
  }

  function parseTimestampMs(value) {
    if (value == null || value === '') return NaN;
    var t = new Date(value).getTime();
    return Number.isFinite(t) ? t : NaN;
  }

  function slotCapacityAsOfMs(slot) {
    if (!slot) return NaN;
    var raw =
      slot.capacity_as_of ||
      (slot._live_state && slot._live_state.capacity_as_of) ||
      null;
    return parseTimestampMs(raw);
  }

  function isLiveDataStale(slot, maxStaleMs) {
    maxStaleMs = Number(maxStaleMs);
    if (!Number.isFinite(maxStaleMs) || maxStaleMs <= 0) return false;
    var asOfMs = slotCapacityAsOfMs(slot);
    if (!Number.isFinite(asOfMs)) return true;
    return Date.now() - asOfMs > maxStaleMs;
  }

  function warnValidateFailure(reason, detail) {
    if (global.__WB_DIAG__ || global.__WCF_DIAG__) {
      console.warn('[WorkshopCityEvents] validateAddToCart:', reason, detail || '');
    }
  }

  function bodyAllowsRequest(body, requestedTotalQty) {
    if (!body || typeof body !== 'object') return false;
    if (body.allowed === true || body.valid === true || body.bookable === true || body.available === true || body.can_book === true) {
      return true;
    }
    if (body.allowed === false || body.valid === false || body.bookable === false || body.available === false || body.can_book === false) {
      return false;
    }
    var cap = body.capacity_remaining;
    if (hasValue(cap) && Number.isFinite(Number(cap))) {
      return Number(cap) >= Math.max(0, Math.floor(Number(requestedTotalQty) || 0));
    }
    return false;
  }

  function bodyHasDecision(body) {
    if (!body || typeof body !== 'object') return false;
    var keys = ['allowed', 'valid', 'bookable', 'available', 'can_book'];
    for (var i = 0; i < keys.length; i += 1) {
      if (typeof body[keys[i]] === 'boolean') return true;
    }
    return hasValue(body.capacity_remaining);
  }

  function postJson(url, body, timeoutMs) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer =
      controller && timeoutMs
        ? setTimeout(function () {
            controller.abort();
          }, timeoutMs)
        : null;
    return fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      mode: 'cors',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined,
    })
      .then(function (res) {
        if (timer) clearTimeout(timer);
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (payload) {
            return { ok: res.ok, status: res.status, body: payload || {} };
          });
      })
      .catch(function () {
        if (timer) clearTimeout(timer);
        return { ok: false, status: 0, body: {} };
      });
  }

  function liveStateRequestKey(gids) {
    return (gids || []).slice().sort().join('\n');
  }

  function fetchLiveState(eventIdsOrSlots, options) {
    options = options || {};
    var gids = Array.isArray(eventIdsOrSlots)
      ? eventIdsOrSlots.every(function (x) {
          return typeof x === 'string';
        })
        ? eventIdsOrSlots
        : collectEventGids(eventIdsOrSlots)
      : [];
    var venueRef = String(options.venue_ref_id || options.venueRefId || '').trim();
    var dateFrom = String(options.date_from || options.dateFrom || '').trim();
    var dateTo = String(options.date_to || options.dateTo || '').trim();
    var hasWindow = !!(venueRef && dateFrom && dateTo);
    if (!gids.length && !hasWindow) return Promise.resolve({ as_of: null, states: {} });

    var timeoutMs = options.timeoutMs != null ? options.timeoutMs : DEFAULT_FETCH_TIMEOUT_MS;
    var root = options.root;
    var force = options.force === true;
    var reuseKey = hasWindow && !gids.length
      ? ['win', venueRef, dateFrom, dateTo, String(options.since || '')].join('|')
      : liveStateRequestKey(gids);
    var recent = liveStateRecent[reuseKey];
    if (
      !force &&
      recent &&
      Date.now() - recent.ts < LIVE_STATE_REUSE_MS &&
      recent.result &&
      recent.result.ok
    ) {
      options._liveOk = true;
      return Promise.resolve(recent.result);
    }
    // Join an identical in-flight request even when force=true: force must
    // bypass the reuse cache above, but a request already on the wire is fresh,
    // so coalescing prevents parallel-sweep connection storms.
    if (liveStateInFlight[reuseKey]) return liveStateInFlight[reuseKey];

    var pending = (
      hasWindow && !gids.length
        ? postJson(
            liveStateUrl(),
            {
              shop_domain: options.shopDomain || shopDomain(),
              venue_ref_id: venueRef,
              date_from: dateFrom,
              date_to: dateTo,
              since: options.since || undefined,
            },
            timeoutMs
          ).then(function (res) {
            return { merged: res && res.body ? res.body : null, results: [res] };
          })
        : requestLiveStateChunks(liveStateUrl(), gids, options, timeoutMs)
    )
      .then(function (payload) {
        var merged = payload.merged;
        if (!merged) {
          return { ok: false, as_of: null, states: {} };
        }
        var states = merged.states || {};
        var hasStates = Object.keys(states).length > 0;
        var result = {
          ok: merged.ok !== false && (hasStates || merged.unchanged === true || !!merged.days),
          unchanged: merged.unchanged === true,
          as_of: merged.as_of || null,
          states: states,
          days: merged.days || null,
          config_version: merged.config_version,
          client_config: merged.client_config,
        };
        var applyMeta = options.applyMeta !== false && root;
        var metaPromise = applyMeta
          ? applyConfigMetaFromResponse(root, merged)
          : Promise.resolve();
        return metaPromise
          .then(function () {
            return result;
          })
          .catch(function () {
            return result;
          });
      })
      .then(function (result) {
        options._liveOk = !!(result && result.ok);
        if (result && result.ok) {
          liveStateRecent[reuseKey] = { ts: Date.now(), result: result };
        }
        return result;
      })
      .catch(function () {
        options._liveOk = false;
        return { ok: false, as_of: null, states: {} };
      })
      .finally(function () {
        if (liveStateInFlight[reuseKey] === pending) delete liveStateInFlight[reuseKey];
      });

    liveStateInFlight[reuseKey] = pending;
    return pending;
  }

  function fetchBookingConfig(options) {
    options = options || {};
    var url = configUrl(options);
    var timeoutMs = options.timeoutMs != null ? options.timeoutMs : DEFAULT_FETCH_TIMEOUT_MS;
    return postJson(
      url,
      {
        shop_domain: options.shopDomain || shopDomain(),
        intent: 'load_workshop_booking_config',
      },
      timeoutMs
    ).then(function (res) {
      var body = res.body || {};
      if (res.ok && body.config) rememberConfigFromBody(body);
      return { ok: res.ok, status: res.status, body: body };
    });
  }

  function ensureBookingConfig(options) {
    if (cachedFullConfig) return Promise.resolve({ ok: true, body: { config: cachedFullConfig } });
    if (configLoadPromise) return configLoadPromise;
    configLoadPromise = fetchBookingConfig(options).finally(function () {
      configLoadPromise = null;
    });
    return configLoadPromise;
  }

  function cartRootPath() {
    return (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
  }

  function eventIdAliases(id) {
    var aliases = [];
    if (id == null || id === '') return aliases;
    var s = String(id).trim();
    if (!s) return aliases;
    function add(value) {
      var v = String(value || '').trim();
      if (!v || aliases.indexOf(v) !== -1) return;
      aliases.push(v);
    }
    add(s);
    var gidMatch = s.match(/^gid:\/\/shopify\/Metaobject\/(\d+)$/i);
    if (gidMatch && gidMatch[1]) {
      add(gidMatch[1]);
      add('gid://shopify/Metaobject/' + gidMatch[1]);
    } else if (/^\d+$/.test(s)) {
      add('gid://shopify/Metaobject/' + s);
    }
    return aliases;
  }

  function collectEventIdAliasesFromProps(props) {
    var aliases = [];
    function addValue(value) {
      eventIdAliases(value).forEach(function (alias) {
        if (aliases.indexOf(alias) === -1) aliases.push(alias);
      });
    }
    if (!props || typeof props !== 'object') return aliases;
    if (!Array.isArray(props)) {
      addValue(props._event_metaobject_gid);
      addValue(props._event_id);
      addValue(props.event_id);
      return aliases;
    }
    for (var i = 0; i < props.length; i += 1) {
      var entry = props[i];
      if (!entry) continue;
      var k = entry[0] != null ? entry[0] : entry.name;
      var v = entry[1] != null ? entry[1] : entry.value;
      if (k === '_event_metaobject_gid' || k === '_event_id' || k === 'event_id') addValue(v);
    }
    return aliases;
  }

  function propsMatchEvent(props, eventIdStr) {
    var targetAliases = eventIdAliases(eventIdStr);
    if (!targetAliases.length) return false;
    var propAliases = collectEventIdAliasesFromProps(props);
    for (var i = 0; i < propAliases.length; i += 1) {
      if (targetAliases.indexOf(propAliases[i]) !== -1) return true;
    }
    return false;
  }

  function isWorkshopAddonProps(props) {
    if (!props || typeof props !== 'object') return false;
    if (!Array.isArray(props)) return props._workshop_addon === 'true';
    for (var i = 0; i < props.length; i += 1) {
      var entry = props[i];
      if (!entry) continue;
      var k = entry[0] != null ? entry[0] : entry.name;
      var v = entry[1] != null ? entry[1] : entry.value;
      if (k === '_workshop_addon' && String(v) === 'true') return true;
    }
    return false;
  }

  function seatsInCartForEvent(eventId, cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    var eventIdStr = String(eventId || '');
    var total = 0;
    cart.items.forEach(function (item) {
      if (!item || isWorkshopAddonProps(item.properties)) return;
      if (propsMatchEvent(item.properties, eventIdStr)) total += item.quantity || 0;
    });
    return total;
  }

  function storageKeyFromId(id) {
    var s = String(id || '').trim();
    if (!s) return '';
    if (/^\d+$/.test(s)) return s;
    var m = s.match(/(\d+)\s*$/);
    return m ? m[1] : '';
  }

  function poolKeysMatch(a, b) {
    var ka = storageKeyFromId(a);
    var kb = storageKeyFromId(b);
    if (ka && kb) return ka === kb;
    var sa = String(a || '').trim();
    var sb = String(b || '').trim();
    return !!(sa && sb && sa === sb);
  }

  function propValueFromProps(props, name) {
    if (!props || typeof props !== 'object') return '';
    if (!Array.isArray(props)) return String(props[name] || '').trim();
    for (var i = 0; i < props.length; i += 1) {
      var entry = props[i];
      if (!entry) continue;
      var k = entry[0] != null ? entry[0] : entry.name;
      var v = entry[1] != null ? entry[1] : entry.value;
      if (String(k) === name) return String(v || '').trim();
    }
    return '';
  }

  function capacityPoolKeyFromSlot(slot) {
    if (!slot) return '';
    var raw = slot._raw && typeof slot._raw === 'object' ? slot._raw : {};
    return (
      String(slot.capacity_group_id || raw.capacity_group_id || '').trim() ||
      String(slot.canonical_event_id || raw.canonical_event_id || '').trim() ||
      String(eventGidFromSlot(slot) || '').trim()
    );
  }

  function capacityPoolKeyFromProps(props) {
    return (
      propValueFromProps(props, '_capacity_group_id') ||
      propValueFromProps(props, '_canonical_event_id') ||
      propValueFromProps(props, '_event_metaobject_gid') ||
      propValueFromProps(props, '_event_id')
    );
  }

  function seatsInCartForPool(slot, cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    var poolKey = capacityPoolKeyFromSlot(slot);
    var eventGid = eventGidFromSlot(slot);
    if (!poolKey && !eventGid) return 0;
    var total = 0;
    cart.items.forEach(function (item) {
      if (!item || isWorkshopAddonProps(item.properties)) return;
      var itemPool = capacityPoolKeyFromProps(item.properties);
      if (poolKey && itemPool && poolKeysMatch(poolKey, itemPool)) {
        total += item.quantity || 0;
        return;
      }
      if (eventGid && propsMatchEvent(item.properties, eventGid)) {
        total += item.quantity || 0;
      }
    });
    return total;
  }

  function fetchCart() {
    return fetch(cartRootPath() + 'cart.js')
      .then(function (res) {
        return res.ok ? res.json().catch(function () { return null; }) : null;
      })
      .catch(function () {
        return null;
      });
  }

  function resolveBookingConfig() {
    return global.WorkshopBookingConfig || global.WorkshopCityConfig;
  }

  function applyConfigMetaFromResponse(root, body) {
    var CFG = resolveBookingConfig();
    if (root && CFG && typeof CFG.applyMetaFromResponse === 'function') {
      return CFG.applyMetaFromResponse(root, body);
    }
    return Promise.resolve();
  }

  function validateCapacity(eventId, requestedTotalQty, options) {
    options = options || {};
    var gid = typeof eventId === 'string' ? eventId : eventGidFromSlot(eventId);
    if (!gid) return Promise.resolve({ ok: false, allowed: false, message: 'Event not found' });
    var url = validateUrl();
    var timeoutMs = options.timeoutMs != null ? options.timeoutMs : DEFAULT_FETCH_TIMEOUT_MS;
    return postJson(
      url,
      {
        shop_domain: options.shopDomain || shopDomain(),
        event_id: gid,
        requested_total_qty: Math.max(0, Math.floor(Number(requestedTotalQty) || 0)),
        intent: options.intent || 'add_to_cart',
      },
      timeoutMs
    )
      .then(function (res) {
        var body = res.body || {};
        var status = normalizeStatus(body.status);
        var displayStatus = normalizeStatus(body.display_status);
        var cap = body.capacity_remaining;
        var blocked =
          status === 'draft' ||
          status === 'completed' ||
          status === 'cancelled' ||
          status === 'sold_out' ||
          status === 'paused' ||
          status === 'unavailable' ||
          status === 'closed' ||
          status === 'stopped' ||
          displayStatus === 'hidden' ||
          displayStatus === 'ended' ||
          displayStatus === 'sold_out' ||
          displayStatus === 'paused' ||
          displayStatus === 'unavailable' ||
          displayStatus === 'closed' ||
          displayStatus === 'stopped' ||
          (hasValue(cap) && Number(cap) <= 0);
        if (!res.ok) {
          return {
            ok: false,
            http_status: res.status,
            status: body.status,
            allowed: false,
            authoritative: true,
            blocked: blocked,
            capacity_remaining: body.capacity_remaining,
            display_status: body.display_status,
            event_status: body.status,
            message: body.message || body.description || 'Capacity validation failed',
          };
        }
        return {
          ok: body.ok !== false,
          http_status: res.status,
          status: body.status,
          allowed: body.ok !== false && bodyAllowsRequest(body, requestedTotalQty) && !blocked,
          authoritative: body.ok !== false && bodyHasDecision(body),
          blocked: blocked,
          capacity_remaining: body.capacity_remaining,
          display_status: body.display_status,
          event_status: body.status,
          capacity_as_of: body.as_of || body.capacity_as_of || null,
          message: body.message || body.description || '',
        };
      })
      .catch(function () {
        if (validateFailModeOpen()) {
          return {
            ok: false,
            allowed: true,
            authoritative: false,
            blocked: false,
            skipped_validation: true,
            validation_unavailable: true,
            message: '',
          };
        }
        return {
          ok: false,
          allowed: false,
          authoritative: false,
          blocked: false,
          validation_unavailable: true,
          message: 'We could not verify workshop capacity right now. Please try again.',
        };
      });
  }

  function validateCapacityWithRetry(eventId, requestedTotalQty, options, retriesLeft) {
    retriesLeft = retriesLeft != null ? retriesLeft : 1;
    return validateCapacity(eventId, requestedTotalQty, options).then(function (result) {
      if (
        result &&
        result.validation_unavailable === true &&
        result.allowed !== true &&
        retriesLeft > 0
      ) {
        return new Promise(function (resolve) {
          global.setTimeout(function () {
            resolve(validateCapacityWithRetry(eventId, requestedTotalQty, options, retriesLeft - 1));
          }, 350);
        });
      }
      return result;
    });
  }

  function ensureFreshSlotForAdd(slot, options) {
    options = options || {};
    var gid = eventGidFromSlot(slot);
    if (!gid) return Promise.resolve({ slot: slot, fetchOk: false, fetchAttempted: false });

    /* Validate is a fresh engine read — skip the extra event-live-state round-trip. */
    if (options.skipLiveFetch === true || !liveFetchBeforeAddEnabled()) {
      return Promise.resolve({ slot: slot, fetchOk: true, fetchAttempted: false });
    }

    var fetchGid = primaryEventIdForRequest((slot && slot.canonical_event_id) || gid);
    var maxStale = maxStaleMsBeforeAdd();

    function fetchAndMerge() {
      return fetchLiveState([fetchGid], options).then(function (liveResult) {
        var fetchOk = !!(liveResult && liveResult.ok !== false);
        var merged =
          mergeSlotWithLive(slot || { gid: gid }, (liveResult && liveResult.states) || {}, liveResult && liveResult.as_of) ||
          slot;
        return { slot: merged, fetchOk: fetchOk, fetchAttempted: true, liveResult: liveResult };
      });
    }

    return fetchAndMerge().then(function (first) {
      if (!first.fetchAttempted || !first.fetchOk) return first;
      if (!isLiveDataStale(first.slot, maxStale)) return first;
      return fetchAndMerge();
    });
  }

  function fetchCapacityValidate(root, params, timeoutMs) {
    params = params || {};
    var intent = params.intent || 'add_to_cart';
    var CFG = resolveBookingConfig();
    if (CFG && typeof CFG.validateGateEnabled === 'function' && !CFG.validateGateEnabled(root, intent)) {
      return Promise.resolve(null);
    }

    var requested =
      params.requested_total_qty != null
        ? params.requested_total_qty
        : params.requestedTotalQty != null
          ? params.requestedTotalQty
          : 0;

    return validateCapacity(params.event_id || params.eventId || params.gid, requested, {
      intent: intent,
      timeoutMs: timeoutMs,
    }).then(function (result) {
      return {
        ok: result && result.ok,
        allowed: !!(result && result.allowed === true),
        capacity_remaining: result && result.capacity_remaining,
        display_status: result && result.display_status,
        status: result && (result.status || result.event_status),
        message: (result && result.message) || '',
      };
    });
  }

  function validateAddToCart(slot, requestedAddQty, options) {
    options = options || {};
    var gid = eventGidFromSlot(slot);
    if (!gid) {
      return Promise.resolve({ allowed: false, message: 'Event not found', blocked: true });
    }
    var addQty = Math.max(0, Math.floor(Number(requestedAddQty) || 0));
    var cartPromise = options.cart ? Promise.resolve(options.cart) : fetchCart();
    return cartPromise
      .then(function (cart) {
        return ensureFreshSlotForAdd(slot, options).then(function (fresh) {
          return { cart: cart, fresh: fresh };
        });
      })
      .then(function (ctx) {
        var cart = ctx.cart;
        var fresh = ctx.fresh || {};
        var mergedSlot = fresh.slot || slot;

        // Cart fetch failed (null = network/HTTP error, empty cart is an object).
        // Treating unknown cart as 0 seats-in-cart would undercount and risk
        // oversell, so fail closed for add-to-cart unless fail-mode is open.
        if (
          cart == null &&
          String(options.intent || 'add_to_cart') === 'add_to_cart' &&
          !validateFailModeOpen()
        ) {
          warnValidateFailure('cart fetch failed', { event_id: gid });
          return {
            allowed: false,
            blocked: false,
            validation_unavailable: true,
            cartData: null,
            message: 'We could not verify your cart right now. Please try again.',
          };
        }

        if (fresh.fetchAttempted && !fresh.fetchOk) {
          warnValidateFailure('event-live-state fetch failed', { event_id: gid });
          if (validateFailModeOpen()) {
            return {
              allowed: true,
              cartData: cart,
              inCart: seatsInCartForPool(mergedSlot, cart),
              requestedAdd: addQty,
              requestedTotal: seatsInCartForPool(mergedSlot, cart) + addQty,
              skipped_validation: true,
              validation_unavailable: true,
            };
          }
          return {
            allowed: false,
            blocked: false,
            validation_unavailable: true,
            cartData: cart,
            message: 'We could not verify workshop capacity right now. Please try again.',
          };
        }

        if (!isSlotBookable(mergedSlot)) {
          return {
            allowed: false,
            blocked: true,
            cartData: cart,
            message:
              normalizeStatus(mergedSlot.display_status) === 'sold_out' ||
              normalizeStatus(mergedSlot.status) === 'sold_out'
                ? 'This workshop is sold out. Please select a different time.'
                : 'This workshop event is no longer available. Please select a different time.',
          };
        }

        var inCart = seatsInCartForPool(mergedSlot, cart);
        if (!shouldValidateBeforeAdd()) {
          return {
            allowed: true,
            cartData: cart,
            inCart: inCart,
            requestedAdd: addQty,
            requestedTotal: inCart + addQty,
            skipped_validation: true,
          };
        }

        var validateGid = primaryEventIdForRequest(mergedSlot.canonical_event_id || gid);
        var validateOpts = Object.assign({}, options, { intent: options.intent || 'add_to_cart' });
        var retries = options.skipLiveFetch === true || options.skipRetry === true ? 0 : 1;
        return validateCapacityWithRetry(validateGid, inCart + addQty, validateOpts, retries).then(function (result) {
          if (result && result.validation_unavailable && result.allowed !== true) {
            warnValidateFailure('capacity/validate unavailable', { event_id: validateGid, http_status: result.http_status });
          }
          result = result || {};
          // No real verdict came back (no decision flags, no capacity number):
          // that is "unverified", not "unavailable" — let the add through.
          if (
            verifySoftMode() &&
            result.allowed !== true &&
            result.authoritative !== true &&
            result.blocked !== true
          ) {
            result.allowed = true;
            result.skipped_validation = true;
            result.message = '';
          }
          result.cartData = cart;
          result.inCart = inCart;
          result.requestedAdd = addQty;
          result.requestedTotal = inCart + addQty;
          return result;
        });
      });
  }

  function liveStateAliasesMatch(a, b) {
    if (!a || !b) return false;
    var aList = eventIdAliases(a);
    var bList = eventIdAliases(b);
    for (var i = 0; i < aList.length; i++) {
      if (bList.indexOf(aList[i]) !== -1) return true;
    }
    return false;
  }

  function findLiveStateForGid(states, gid) {
    if (!states || !gid) return null;
    if (states[gid]) return states[gid];
    var aliases = eventIdAliases(gid);
    for (var i = 0; i < aliases.length; i++) {
      if (states[aliases[i]]) return states[aliases[i]];
    }
    var keys = Object.keys(states);
    for (var k = 0; k < keys.length; k++) {
      if (liveStateAliasesMatch(gid, keys[k])) return states[keys[k]];
    }
    return null;
  }

  function mergeSlotWithLive(slot, states, asOf) {
    if (!slot) return null;
    var gid = eventGidFromSlot(slot);
    if (!gid) return slot;
    var live = findLiveStateForGid(states, gid);
    if (!live) return slot;

    var merged = {};
    Object.keys(slot).forEach(function (key) {
      merged[key] = slot[key];
    });

    var raw = {};
    if (slot._raw && typeof slot._raw === 'object') {
      Object.keys(slot._raw).forEach(function (key) {
        raw[key] = slot._raw[key];
      });
    }

    if (live.canonical_event_id != null) merged.canonical_event_id = live.canonical_event_id;
    if (live.capacity_group_id != null) merged.capacity_group_id = live.capacity_group_id;
    if (live.display_status != null) merged.display_status = live.display_status;
    if (live.status != null) merged.status = normalizeStatus(live.status);
    if (live.capacity_remaining != null) merged.capacity_remaining = live.capacity_remaining;

    if (asOf) merged.capacity_as_of = asOf;
    merged._live_state = live;
    merged._live_state_fresh = true;

    ['canonical_event_id', 'capacity_group_id', 'display_status', 'status', 'capacity_remaining'].forEach(
      function (key) {
        if (hasValue(merged[key])) raw[key] = merged[key];
      }
    );
    merged._raw = raw;
    return reconcileMergedSlotLiveStatus(merged);
  }

  function liveAvailabilityFlags(live) {
    live = live || {};
    return {
      explicitBookable:
        live.bookable === true || live.available === true || live.can_book === true,
      explicitNotBookable:
        live.bookable === false || live.available === false || live.can_book === false,
    };
  }

  function reconcileMergedSlotLiveStatus(merged) {
    if (!merged || !merged._live_state_fresh) return merged;
    var cap = slotCapacityRemaining(merged);
    var live = merged._live_state || {};
    var liveStatus = normalizeStatus(live.status);
    var liveDisplay = normalizeStatus(live.display_status);
    var flags = liveAvailabilityFlags(live);
    var displayBookable = isDisplayStatusBookable(liveDisplay) === true;

    if (liveStatus === 'sold_out' || liveDisplay === 'sold_out') {
      merged.status = 'sold_out';
      merged.display_status = 'sold_out';
    } else if (cap != null && cap > 0 && (displayBookable || flags.explicitBookable)) {
      if (
        liveStatus === 'cancelled' ||
        liveStatus === 'completed' ||
        liveStatus === 'draft' ||
        !liveStatus
      ) {
        merged.status = 'active';
      } else {
        merged.status = liveStatus;
      }
      merged.display_status = live.display_status || 'bookable';
    } else if (flags.explicitNotBookable || liveStatus === 'sold_out' || liveDisplay === 'sold_out') {
      merged.status = 'sold_out';
      merged.display_status = 'sold_out';
    } else if (cap != null && cap <= 0) {
      merged.status = 'sold_out';
      if (liveDisplay !== 'hidden' && liveDisplay !== 'ended') merged.display_status = 'sold_out';
    } else if (cap != null && cap > 0) {
      if (
        liveStatus === 'cancelled' ||
        liveStatus === 'completed' ||
        liveStatus === 'draft' ||
        !liveStatus
      ) {
        merged.status = 'active';
      } else if (liveStatus) {
        merged.status = liveStatus;
      }

      if (liveDisplay && liveDisplay !== 'sold_out') merged.display_status = live.display_status;
      else if (
        flags.explicitBookable ||
        isDisplayStatusBookable(merged.display_status) === null
      ) {
        merged.display_status = 'bookable';
      }
    } else if (flags.explicitBookable && cap == null) {
      merged.display_status = live.display_status || 'bookable';
    }

    if (merged._raw && typeof merged._raw === 'object') {
      ['status', 'display_status', 'capacity_remaining'].forEach(function (key) {
        if (hasValue(merged[key])) merged._raw[key] = merged[key];
      });
    }
    return merged;
  }

  function mergeSlotsWithLive(slots, liveMapOrOptions, maybeAsOf) {
    var list = Array.isArray(slots) ? slots : [];
    if (liveMapOrOptions && typeof liveMapOrOptions.then === 'function') {
      return liveMapOrOptions.then(function (result) {
        return mergeSlotsWithLive(list, (result && result.states) || {}, result && result.as_of);
      });
    }
    if (
      liveMapOrOptions &&
      (liveMapOrOptions.fetch === true || liveMapOrOptions.url || liveMapOrOptions.apiBase)
    ) {
      return fetchLiveState(list, liveMapOrOptions)
        .then(function (result) {
          return mergeSlotsWithLive(list, result.states || {}, result.as_of);
        })
        .catch(function () {
          return list;
        });
    }
    var states = liveMapOrOptions || {};
    var asOf = maybeAsOf;
    return list
      .map(function (slot) {
        return mergeSlotWithLive(slot, states, asOf);
      })
      .filter(Boolean);
  }

  function isDisplayStatusBookable(displayStatus) {
    var d = normalizeStatus(displayStatus);
    if (!d) return null;
    if (
      d === 'sold_out' ||
      d === 'hidden' ||
      d === 'ended' ||
      d === 'cancelled' ||
      d === 'draft' ||
      d === 'completed' ||
      d === 'paused' ||
      d === 'unavailable' ||
      d === 'closed' ||
      d === 'stopped'
    ) {
      return false;
    }
    if (d === 'bookable' || d === 'active' || d === 'available' || d === 'open') return true;
    return null;
  }

  function slotCapacityRemaining(slot) {
    if (!slot) return null;
    var cap = slot.capacity_remaining;
    if (!hasValue(cap) && slot._raw) cap = slot._raw.capacity_remaining;
    if (!hasValue(cap)) return null;
    var n = Number(cap);
    return Number.isFinite(n) ? n : null;
  }

  function isSlotBookableFromCatalog(slot) {
    if (!slot) return false;
    var liveStatus = normalizeStatus(slot.status);
    var liveDisplay = normalizeStatus(slot.display_status);
    if (liveDisplay === 'hidden' || liveDisplay === 'ended') return false;
    if (liveStatus === 'draft' || liveStatus === 'completed' || liveStatus === 'cancelled') return false;
    if (liveStatus === 'sold_out' || liveDisplay === 'sold_out') return false;
    var cap = slotCapacityRemaining(slot);
    if (cap != null && cap <= 0) return false;
    if (isDisplayStatusBookable(liveDisplay) === true && cap != null && cap > 0) return true;
    return isDisplayStatusBookable(liveDisplay) === true;
  }

  function isSlotBookable(slot) {
    if (!slot) return false;

    if (slot._live_state_fresh && slot._live_state) {
      var cap = slotCapacityRemaining(slot);
      var live = slot._live_state;
      var flags = liveAvailabilityFlags(live);
      var liveStatus = normalizeStatus(live.status);
      var liveDisplay = normalizeStatus(live.display_status);
      var displayBookable = isDisplayStatusBookable(liveDisplay) === true;

      if (liveDisplay === 'hidden' || liveDisplay === 'ended') return false;
      if (liveStatus === 'draft' || liveStatus === 'completed' || liveStatus === 'cancelled') return false;
      if (flags.explicitNotBookable || liveStatus === 'sold_out' || liveDisplay === 'sold_out') return false;
      if (cap != null && cap <= 0) return false;
      if (cap != null && cap > 0 && (displayBookable || flags.explicitBookable)) return true;
      if (isDisplayStatusBookable(liveDisplay) === false) return false;
      if (cap != null && cap > 0) return true;
      return flags.explicitBookable;
    }

    return isSlotBookableFromCatalog(slot);
  }

  global.WorkshopCityEvents = Object.assign(global.WorkshopCityEvents || {}, {
    collectEventGids: collectEventGids,
    eventGidFromSlot: eventGidFromSlot,
    eventIdAliases: eventIdAliases,
    collectEventIdAliasesFromProps: collectEventIdAliasesFromProps,
    capacityPoolKeyFromSlot: capacityPoolKeyFromSlot,
    capacityPoolKeyFromProps: capacityPoolKeyFromProps,
    seatsInCartForEvent: seatsInCartForEvent,
    seatsInCartForPool: seatsInCartForPool,
    fetchCart: fetchCart,
    fetchLiveState: fetchLiveState,
    fetchBookingConfig: fetchBookingConfig,
    ensureBookingConfig: ensureBookingConfig,
    validateCapacity: validateCapacity,
    fetchCapacityValidate: fetchCapacityValidate,
    validateAddToCart: validateAddToCart,
    getClientConfig: getClientConfig,
    getFullConfig: getFullConfig,
    resolvePollIntervalMs: resolvePollIntervalMs,
    shouldValidateBeforeAdd: shouldValidateBeforeAdd,
    isSlotBookable: isSlotBookable,
    mergeSlotWithLive: mergeSlotWithLive,
    mergeSlotsWithLive: mergeSlotsWithLive,
    normalizeStatus: normalizeStatus,
    resolveLiveStateUrl: liveStateUrl,
    resolveLiveStateApiUrl: liveStateUrl,
    deriveLiveStateApiUrl: liveStateUrl,
    resolveLiveStreamUrl: liveStreamUrl,
    resolveValidateUrl: validateUrl,
    resolveConfigUrl: configUrl,
    get LIVE_STATE_URL() {
      return liveStateUrl();
    },
    get LIVE_STREAM_URL() {
      return liveStreamUrl();
    },
    get VALIDATE_URL() {
      return validateUrl();
    },
    get CONFIG_URL() {
      return configUrl();
    },
    applyConfigMetaFromResponse: applyConfigMetaFromResponse,
  });
})(typeof window !== 'undefined' ? window : this);
